<template>
  <b-card
    ref="userList"
    title="Users"
  >
    <!-- search input -->
    <div class="custom-search d-flex justify-content-end">
      <b-form-group>
        <div class="d-flex align-items-center">
          <label class="mr-1">Search</label>
          <b-form-input
            v-model="searchTerm"
            placeholder="Search"
            type="text"
            class="d-inline-block"
            @input="handleSearch"
          />
        </div>
      </b-form-group>
      <b-form-group class="ml-1">
        <b-button
          variant="primary"
          :to="{ name: 'account-admin-user-add'}"
        >
          <span class="text-nowrap">Add User</span>
        </b-button>
      </b-form-group>
    </div>

    <!-- tables -->
    <vue-good-table
      :columns="columns"
      :rows="rows"
      :rtl="direction"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm
      }"
      :select-options="{
        enabled: false,
        selectOnCheckboxOnly: true,
        selectionInfoClass: 'custom-class',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true,
        selectAllByGroup: true,
      }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength
      }"
      @on-sort-change="onSortChange"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- Column: Name -->
        <span
          v-if="props.column.field === 'firstName'"
          class="text-nowrap"
        >
          <span class="text-nowrap">{{ props.row.firstName }} {{ props.row.lastName }}</span>
        </span>

        <!-- Column: Account Roles -->
        <span
          v-else-if="props.column.field === 'accountRoles'"
          class="text-nowrap"
        >
          <ul
            v-if="props.row.userAccounts"
          >
            <li
              v-for="(accountRole) in props.row.userAccounts[Object.keys(props.row.userAccounts)[0]].userAccountRoles"
              :key="accountRole.id"
            >{{ accountRole.name }} ({{ accountRole.description }})</li>
          </ul>
        </span>

        <!-- Column: Project Roles -->
        <span
          v-else-if="props.column.field === 'projectRoles'"
          class="text-nowrap"
        >
          <ul v-if="props.row.userAccounts">
            <li
              v-for="(userProject) in props.row.userAccounts[Object.keys(props.row.userAccounts)[0]].userProjects"
              :key="userProject.id"
            >
              <span>{{ userProject.projectName }} </span>
              <ul>
                <li
                  v-for="(userRole) in userProject.userRoles"
                  :key="userRole.id"
                >{{ userRole.name }} ({{ userRole.description }})</li>
              </ul>
            </li>
          </ul>
        </span>
        <!-- Column: Action -->
        <span v-else-if="props.column.field === 'action'">
          <span>
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle mr-25"
                />
              </template>
              <b-dropdown-item :to="{ name: 'account-admin-user-edit', params: { account_user_id: props.row.accountUserId } }">
                <feather-icon
                  icon="Edit2Icon"
                  class="mr-50"
                />
                <span>Edit</span>
              </b-dropdown-item>
              <b-dropdown-item
                @click="deleteUser(props.row.accountUserId, props.row.email)"
              >
                <feather-icon
                  icon="TrashIcon"
                  class="mr-50"
                />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">

          <!-- page length -->
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap ">
              Showing 1 to
            </span>
            <b-form-select
              v-model="pageLength"
              :options="pages"
              class="mx-1"
              @input="(value)=>props.perPageChanged({currentPerPage:value})"
            />
            <span class="text-nowrap"> of {{ props.total }} entries </span>
          </div>

          <!-- pagination -->
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @change="handleChangePage"
              @input="(value)=>props.pageChanged({currentPage:value})"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>

  </b-card>
</template>

<script>
import {
    BCard, BPagination, BFormGroup, BFormInput, BFormSelect, BDropdownItem, BDropdown, BButton,
} from 'bootstrap-vue'

import { VueGoodTable } from 'vue-good-table'
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import Prism from 'vue-prism-component'
import store from '@/store'
import { deleteAccountUser, getAccountUsers } from '@/coreApi/coreApiAccount'

export default {
    components: {
        BCard,
        VueGoodTable,
        BPagination,
        BFormGroup,
        BFormInput,
        BFormSelect,
        Prism,
        BDropdownItem,
        BDropdown,
        BButton,
    },
    data() {
        return {
            account: [],
            pageLength: 10,
            dir: false,
            pages: ['10', '50', '100'],
            columns: [
                {
                    label: 'Name',
                    field: 'firstName',
                },
                {
                    label: 'Email',
                    field: 'email',
                },
                {
                    label: 'Account Roles',
                    field: 'accountRoles',
                    sortable: false,
                },
                {
                    label: 'Project Roles',
                    field: 'projectRoles',
                    sortable: false,
                },
                {
                    label: 'Action',
                    field: 'action',
                    sortable: false,
                },
            ],
            rows: [],
            searchTerm: '',
        }
    },
    computed: {
        direction() {
            if (store.state.appConfig.isRTL) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.dir = true
                return this.dir
            }
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.dir = false
            return this.dir
        },
    },
    created() {
        this.setUserList()
    },
    methods: {
        setUserList() {
            getAccountUsers()
                .then(res => {
                    if (res.data) {
                        this.account = res.data.account
                        this.rows = Object.values(res.data.users)
                    }
                })
        },
        deleteUser(accountUserId, email) {
            this.$bvModal
                .msgBoxConfirm(`Please confirm that you want to delete ${email}.`, {
                    title: 'Please Confirm',
                    size: 'sm',
                    okVariant: 'primary',
                    okTitle: 'Yes',
                    cancelTitle: 'No',
                    cancelVariant: 'outline-secondary',
                    hideHeaderClose: false,
                    centered: true,
                })
                .then(value => {
                    if (value) {
                        deleteAccountUser(accountUserId)
                            .then(() => {
                                this.setUserList()
                            })
                    }
                })
        },
        handleSearch(searching) {
        },
        handleChangePage(page) {
        },
        handlePageChange(active) {
        },
        onSortChange(params) {
        },
    },
}
</script>

<style lang="scss" >
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
